import React, { useContext, useEffect, useMemo, useState } from "react";
import { Handle, useReactFlow } from "react-flow-renderer";
import { Button, Form, Modal } from "react-bootstrap";
import { NodesContext } from "../provider/IvrContext";
import { toast } from "react-toastify";
import { getApiCall } from "../../../../../service/httpCall";
import {
  API_AGENTS_LIST,
  API_AGENT_GROUPS_LIST,
} from "../../../../../service/url";
import {
  BACKEND_API_BASE_URL,
  VOICEMAIL_PROMPT_CATEGORIES,
  VOICEMAIL_MAX_DURATION,
  VOICEMAIL_MIN_DURATION,
  TRANSFER_DEFAULT_OPTIONS,
} from "../../../../../utilities/constant";

const convertToMinutes = (time) => {
  if (!time || !time.includes(":")) return 0;
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const FULL_DAY_MINUTES = 1440;

const computeCoverage = (slots) =>
  slots.reduce((acc, s) => {
    if (!s.startTime || !s.endTime) return acc;

    const st = convertToMinutes(s.startTime);
    let en = convertToMinutes(s.endTime);

    // overnight slot handling
    if (en <= st) {
      en += 1440;
    }

    return acc + (en - st);
  }, 0);

const TransferNode = ({ data, id }) => {
  const { updateNodeData, onRemove } = useContext(NodesContext);
  const { setNodes, setEdges, getNode } = useReactFlow();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [groupList, setGroupList] = useState([]);
  const [agentList, setAgentList] = useState([]);
  const [promptList, setPromptList] = useState([]);

  const [timeSlots, setTimeSlots] = useState(
    data.timeSlots?.length
      ? data.timeSlots.map((slot) => ({
          startTime: slot.start_time || slot.startTime || "",
          endTime: slot.end_time || slot.endTime || "",
          type: slot.type?.replace("transfer_", "") || "",
          transferId: slot.id || "",
        }))
      : [
          {
            startTime: "",
            endTime: "",
            type: "",
            transferId: "",
          },
        ]
  );

  const initialDefault = data.defaultAction || {};
  const [draftDefaultMode, setDraftDefaultMode] = useState(initialDefault.mode || "");
  const [draftDefaultAction, setDraftDefaultAction] = useState(initialDefault.action || {});
  const [draftDefaultCategory, setDraftDefaultCategory] = useState(initialDefault.action?.prompt_category || "");
  const [draftDefaultDuration, setDraftDefaultDuration] = useState(initialDefault.max_duration || "");
  const [draftDefaultUseMedia, setDraftDefaultUseMedia] = useState(Boolean(initialDefault.use_media));
  const [defaultErrors, setDefaultErrors] = useState({});

  const coverageMins = useMemo(() => computeCoverage(timeSlots), [timeSlots]);
  const isFullDay = coverageMins >= FULL_DAY_MINUTES;

  const savedSlots = data.timeSlots || [];
  const savedCoverage = useMemo(
    () =>
      computeCoverage(
        savedSlots.map((s) => ({
          startTime: s.start_time || s.startTime || "",
          endTime: s.end_time || s.endTime || "",
        }))
      ),
    [savedSlots]
  );
  const savedFullDay = savedCoverage >= FULL_DAY_MINUTES;

  const addTimeSlot = () => {
    setTimeSlots((prev) => [
      ...prev,
      { startTime: "", endTime: "", type: "", transferId: "" },
    ]);
  };

  const removeTimeSlot = (index) => {
    setTimeSlots((prev) => prev.filter((_, i) => i !== index));
  };

  const updateTimeSlot = (index, key, value) => {
    const updated = [...timeSlots];
    updated[index][key] = value;
    if (key === "type") updated[index].transferId = "";
    setTimeSlots(updated);
  };

  const isTimeOverlapping = (currentIndex, start, end) => {
  if (!start || !end) return false;

  let startMinutes = convertToMinutes(start);
  let endMinutes = convertToMinutes(end);

  if (endMinutes <= startMinutes) {
    endMinutes += 1440;
  }

  return timeSlots.some((slot, index) => {
    if (index === currentIndex) return false;
    if (!slot.startTime || !slot.endTime) return false;

    let slotStart = convertToMinutes(slot.startTime);
    let slotEnd = convertToMinutes(slot.endTime);

    if (slotEnd <= slotStart) {
      slotEnd += 1440;
    }

    return (
      startMinutes < slotEnd &&
      endMinutes > slotStart
    );
  });
};

  useEffect(() => {
    getApiCall(API_AGENT_GROUPS_LIST).then(setGroupList).catch(console.log);
    getApiCall(API_AGENTS_LIST).then(setAgentList).catch(console.log);
    if (data.userId) {
      getApiCall(`${BACKEND_API_BASE_URL}/v2/prompt?prompt_status=1&user_id=${data.userId}`)
        .then((resp) => {
          if (Array.isArray(resp)) {
            setPromptList(resp.filter((p) => VOICEMAIL_PROMPT_CATEGORIES.includes(p.prompt_category)));
          }
        })
        .catch(console.log);
    }
  }, [data.userId]);

  // Legacy migration: collapse pre-existing detached defaultNode child into inline defaultAction.
  useEffect(() => {
    const existingId = data.defaultNodeId;
    if (!existingId) return;
    const child = getNode(existingId);
    if (child) {
      const migrated = {
        mode: child.data?.mode || "",
        action: child.data?.action || {},
        max_duration: child.data?.max_duration ?? null,
        use_media: !!child.data?.use_media,
      };
      setEdges((eds) => eds.filter((e) => !(e.source === id && e.target === existingId)));
      setNodes((nds) => nds.filter((n) => n.id !== existingId));
      updateNodeData(id, { defaultAction: migrated, defaultNodeId: null });
      setDraftDefaultMode(migrated.mode);
      setDraftDefaultAction(migrated.action);
      setDraftDefaultCategory(migrated.action?.prompt_category || "");
      setDraftDefaultDuration(migrated.max_duration || "");
      setDraftDefaultUseMedia(!!migrated.use_media);
    } else {
      updateNodeData(id, { defaultNodeId: null });
    }
  }, []);

  const resetDraftDefaultFromData = () => {
    const d = data.defaultAction || {};
    setDraftDefaultMode(d.mode || "");
    setDraftDefaultAction(d.action || {});
    setDraftDefaultCategory(d.action?.prompt_category || "");
    setDraftDefaultDuration(d.max_duration || "");
    setDraftDefaultUseMedia(Boolean(d.use_media));
    setDefaultErrors({});
  };

  const openModal = () => {
    resetDraftDefaultFromData();
    setModalIsOpen(true);
  };

  const handleDefaultModeChange = (e) => {
    const m = e.target.value;
    setDraftDefaultMode(m);
    setDraftDefaultAction({});
    setDraftDefaultCategory("");
    setDraftDefaultDuration("");
    setDraftDefaultUseMedia(false);
  };

  const handleDefaultUseMediaToggle = (e) => {
    const checked = e.target.checked;
    setDraftDefaultUseMedia(checked);
    if (!checked) {
      setDraftDefaultCategory("");
      setDraftDefaultAction({});
    }
  };

  const handleDefaultCategoryChange = (e) => {
    const cat = e.target.value;
    setDraftDefaultCategory(cat);
    setDraftDefaultAction((prev) => ({
      ...prev,
      prompt_category: cat,
      id: "",
      audio: "",
      name: "",
      duration: 0,
    }));
  };

  const handleDefaultAudioChange = (e) => {
    const selected = promptList.find((a) => a.id.toString() === e.target.value);
    if (!selected) {
      setDraftDefaultAction((prev) => ({ ...prev, id: "", audio: "", name: "", duration: 0 }));
      return;
    }
    setDraftDefaultAction({
      type: draftDefaultMode,
      prompt_category: draftDefaultCategory,
      id: selected.id,
      audio: selected.prompt_url,
      duration: selected.prompt_duration || 0,
      name: selected.prompt_name,
    });
  };

  const handleDefaultDurationChange = (e) => {
    const val = e.target.value;
    if (/^\d*$/.test(val) && (val === "" || Number(val) <= VOICEMAIL_MAX_DURATION)) {
      setDraftDefaultDuration(val);
    }
  };

  const validateDefault = (fullDay) => {
    if (fullDay) return true;
    const newErrors = {};
    if (!draftDefaultMode) newErrors.mode = "Action required";
    if (draftDefaultMode === "hangup_with_media") {
      if (draftDefaultCategory && !draftDefaultAction?.id) {
        newErrors.audio = "Audio required when category selected";
      }
    }
    if (draftDefaultMode === "voicemail") {
      if (draftDefaultUseMedia && draftDefaultCategory && !draftDefaultAction?.id) {
        newErrors.audio = "Audio required when category selected";
      }
      if (!draftDefaultDuration || Number(draftDefaultDuration) < VOICEMAIL_MIN_DURATION) {
        newErrors.duration = `Min ${VOICEMAIL_MIN_DURATION}s`;
      } else if (Number(draftDefaultDuration) > VOICEMAIL_MAX_DURATION) {
        newErrors.duration = `Max ${VOICEMAIL_MAX_DURATION}s`;
      }
    }
    setDefaultErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildDefaultActionPayload = () => {
    const payload = { mode: draftDefaultMode };
    if (draftDefaultMode === "hangup") {
      payload.action = {};
      payload.max_duration = null;
      payload.use_media = false;
    } else if (draftDefaultMode === "voicemail") {
      payload.action = draftDefaultUseMedia ? draftDefaultAction : {};
      payload.max_duration = Number(draftDefaultDuration);
      payload.use_media = draftDefaultUseMedia;
    } else {
      payload.action = draftDefaultAction;
      payload.max_duration = null;
      payload.use_media = false;
    }
    return payload;
  };

  const handleSave = () => {
    for (const slot of timeSlots) {
      if (!slot.startTime || !slot.endTime || !slot.type || !slot.transferId) {
        toast.error("Please fill all fields");
        return;
      }
    }

    const fullDay = coverageMins >= FULL_DAY_MINUTES;
    if (!validateDefault(fullDay)) return;

    const finalSlots = timeSlots.map((slot) => {
      const sourceList = slot.type === "group" ? groupList : agentList;
      const selected = sourceList.find(
        (x) => x.id.toString() === slot.transferId.toString()
      );
      return {
        start_time: slot.startTime,
        end_time: slot.endTime,
        type: `transfer_${slot.type}`,
        id: selected?.id,
        name: slot.type === "group" ? selected?.group_name : selected?.agent_name,
      };
    });

    const updates = { timeSlots: finalSlots };
    if (fullDay) {
      updates.defaultAction = null;
    } else {
      updates.defaultAction = buildDefaultActionPayload();
    }
    updates.defaultNodeId = null;

    updateNodeData(id, updates);
    toast.success("Details updated successfully.");
    setModalIsOpen(false);
  };

  const handleRemove = () => {
    onRemove(id);
  };

  const filteredAudio = promptList.filter(
    (p) => !draftDefaultCategory || p.prompt_category === draftDefaultCategory
  );

  const savedDefaultMode = data.defaultAction?.mode;
  const savedDefaultLabel =
    TRANSFER_DEFAULT_OPTIONS.find((o) => o.value === savedDefaultMode)?.label || "";

  return (
    <div style={{ position: "relative", width: "80px", minHeight: 40 }}>
      <div
        className="ivr-flow-node"
        style={{
          position: "relative",
          width: "100%",
          border: "1.5px solid orange",
          borderRadius: "10px",
          background: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "11px",
          padding: "4px",
        }}
      >
        <Handle type="target" position="top" style={{ background: "orange" }} />
        <Handle
          type="source"
          position="bottom"
          style={{ background: "orange", opacity: 0, pointerEvents: "none" }}
          isConnectable={false}
        />

        <span
          className="ivr-flow-node-label"
          style={{
            fontSize: "8px",
            fontWeight: "bold",
            textTransform: "capitalize",
          }}
        >
          {data.label}
        </span>
        {!savedFullDay && savedDefaultLabel && (
          <span style={{ fontSize: "7px", color: "#a35a00", textAlign: "center" }}>
            default: {savedDefaultLabel}
          </span>
        )}

        <div style={{ display: "flex", gap: "10px" }}>
          <i
            className="fa fa-cog ivr-flow-node-icon"
            style={{ cursor: "pointer", color: "orange" }}
            onClick={openModal}
          />
          <i
            className="fa fa-trash ivr-flow-node-icon"
            style={{ cursor: "pointer", color: "orange" }}
            onClick={handleRemove}
          />
        </div>
      </div>

      <Modal
        show={modalIsOpen}
        onHide={() => setModalIsOpen(false)}
        backdrop={false}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Transfer</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {timeSlots.map((slot, index) => {
              const overlap = isTimeOverlapping(index, slot.startTime, slot.endTime);
              return (
                <div
                  key={index}
                  style={{
                    border: "1px solid #dddddd65",
                    padding: "12px",
                    borderRadius: "8px",
                    marginBottom: "15px",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Time Slot {index + 1}</strong>
                    {timeSlots.length > 1 && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => removeTimeSlot(index)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>

                  <Form.Group className="mb-2">
                    <Form.Label>
                      Start Time <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="time"
                      value={slot.startTime}
                      onChange={(e) =>
                        updateTimeSlot(index, "startTime", e.target.value)
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>
                      End Time <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="time"
                      value={slot.endTime}
                      onChange={(e) =>
                        updateTimeSlot(index, "endTime", e.target.value)
                      }
                    />
                  </Form.Group>

                  {overlap && (
                    <div
                      style={{ color: "red", fontSize: "12px", marginBottom: "10px" }}
                    >
                      Time slot overlaps with another slot
                    </div>
                  )}

                  <Form.Group className="mb-2">
                    <Form.Label>
                      Select Type <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      as="select"
                      value={slot.type}
                      onChange={(e) => updateTimeSlot(index, "type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="group">Call Group</option>
                      <option value="agent">Agent</option>
                    </Form.Control>
                  </Form.Group>

                  {slot.type === "group" && (
                    <Form.Group className="mb-2">
                      <Form.Label>
                        Select Group <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        as="select"
                        value={slot.transferId}
                        onChange={(e) =>
                          updateTimeSlot(index, "transferId", e.target.value)
                        }
                      >
                        <option value="">Select Group</option>
                        {groupList.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.group_name}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  )}

                  {slot.type === "agent" && (
                    <Form.Group className="mb-2">
                      <Form.Label>
                        Select Agent <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        as="select"
                        value={slot.transferId}
                        onChange={(e) =>
                          updateTimeSlot(index, "transferId", e.target.value)
                        }
                      >
                        <option value="">Select Agent</option>
                        {agentList.map((agent) => (
                          <option key={agent.id} value={agent.id}>
                            {agent.agent_name}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  )}
                </div>
              );
            })}

            <Button variant="success" onClick={addTimeSlot}>
              + Add Time Slot
            </Button>

            <div
              style={{
                marginTop: 20,
                padding: 12,
                background: "#f8f9fa",
                borderRadius: 8,
              }}
            >
              <strong>Day Coverage:</strong> {coverageMins} / {FULL_DAY_MINUTES} mins{" "}
              {isFullDay
                ? "(Full day covered — default not needed)"
                : "(Configure default action below for uncovered time)"}
            </div>

            {!isFullDay && (
              <div
                style={{
                  marginTop: 20,
                  padding: 12,
                  border: "1px solid #ffc107",
                  borderRadius: 8,
                  // background: "#fffbea",
                }}
              >
                <h6 style={{ marginBottom: 12 }}>Default Action (Uncovered Time)</h6>

                <Form.Group>
                  <Form.Label>
                    Action<Form.Text className="text-danger">*</Form.Text>
                  </Form.Label>
                  <Form.Control
                    as="select"
                    value={draftDefaultMode}
                    onChange={handleDefaultModeChange}
                  >
                    <option value="">Select Action</option>
                    {TRANSFER_DEFAULT_OPTIONS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </Form.Control>
                  {defaultErrors.mode && (
                    <Form.Text className="text-danger">{defaultErrors.mode}</Form.Text>
                  )}
                </Form.Group>

                {draftDefaultMode === "hangup_with_media" && (
                  <>
                    <Form.Group style={{ marginTop: 15 }}>
                      <Form.Label>Prompt Category</Form.Label>
                      <Form.Control
                        as="select"
                        value={draftDefaultCategory}
                        onChange={handleDefaultCategoryChange}
                      >
                        <option value="">Select Category</option>
                        {VOICEMAIL_PROMPT_CATEGORIES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>

                    <Form.Group style={{ marginTop: 15 }}>
                      <Form.Label>Select Audio</Form.Label>
                      <Form.Control
                        as="select"
                        value={draftDefaultAction?.id || ""}
                        onChange={handleDefaultAudioChange}
                        disabled={!draftDefaultCategory}
                      >
                        <option value="">Select Audio</option>
                        {filteredAudio.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.prompt_name}
                          </option>
                        ))}
                      </Form.Control>
                      {defaultErrors.audio && (
                        <Form.Text className="text-danger">{defaultErrors.audio}</Form.Text>
                      )}
                    </Form.Group>
                  </>
                )}

                {draftDefaultMode === "voicemail" && (
                  <>
                    <Form.Group style={{ marginTop: 15 }}>
                      <Form.Label>
                        Max Voicemail Duration (sec, {VOICEMAIL_MIN_DURATION}-
                        {VOICEMAIL_MAX_DURATION})
                        <Form.Text className="text-danger">*</Form.Text>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        min={VOICEMAIL_MIN_DURATION}
                        max={VOICEMAIL_MAX_DURATION}
                        value={draftDefaultDuration}
                        onChange={handleDefaultDurationChange}
                      />
                      {defaultErrors.duration && (
                        <Form.Text className="text-danger">{defaultErrors.duration}</Form.Text>
                      )}
                    </Form.Group>

                    <Form.Group style={{ marginTop: 15 }}>
                      <Form.Check
                        type="checkbox"
                        id={`def-use-media-${id}`}
                        label="Use Media"
                        checked={draftDefaultUseMedia}
                        onChange={handleDefaultUseMediaToggle}
                      />
                    </Form.Group>

                    {draftDefaultUseMedia && (
                      <>
                        <Form.Group style={{ marginTop: 15 }}>
                          <Form.Label>Prompt Category</Form.Label>
                          <Form.Control
                            as="select"
                            value={draftDefaultCategory}
                            onChange={handleDefaultCategoryChange}
                          >
                            <option value="">Select Category</option>
                            {VOICEMAIL_PROMPT_CATEGORIES.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </Form.Control>
                        </Form.Group>

                        <Form.Group style={{ marginTop: 15 }}>
                          <Form.Label>Select Audio</Form.Label>
                          <Form.Control
                            as="select"
                            value={draftDefaultAction?.id || ""}
                            onChange={handleDefaultAudioChange}
                            disabled={!draftDefaultCategory}
                          >
                            <option value="">Select Audio</option>
                            {filteredAudio.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.prompt_name}
                              </option>
                            ))}
                          </Form.Control>
                          {defaultErrors.audio && (
                            <Form.Text className="text-danger">{defaultErrors.audio}</Form.Text>
                          )}
                        </Form.Group>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            onClick={handleSave}
            disabled={timeSlots.some(
              (slot, index) =>
                !slot.startTime ||
                !slot.endTime ||
                !slot.type ||
                !slot.transferId ||
                isTimeOverlapping(index, slot.startTime, slot.endTime)
            )}
          >
            Save
          </Button>
          <Button variant="secondary" onClick={() => setModalIsOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TransferNode;
